import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { LayoutConfig } from '../../../models/user-config/DisplayModeConfig';
import UniqueId from '../../uid/UniqueId';
import UUIDService from '../../uid/UUIDService';
import DatasetTreeFactory from '../dataset-tree/DatasetTreeFactory';
import IDatasetLayoutStrategy from '../dataset-tree/IDatasetLayoutStrategy';
import CategoryIntlProvider from '../intl/CategoryIntlProvider';
import CategoryLayoutViewModel from '../view-model/CategoryLayoutViewModel';
import CategoryViewModel, {
  CategoryTreeType,
} from '../view-model/CategoryViewModel';
import CategoryViewModelSource from '../view-model/CategoryViewModelSource';
import { DatasetLayoutViewModelType } from '../view-model/DatasetLayoutViewModel';

import DatasetTitlesTranslator from './DatasetTitlesTranslator';
import ViewModelCompositor from './ViewModelCompositor';

export const decorateDSInLayoutWithTabIndex = (
  layout: CategoryLayoutViewModel,
  el?: UniqueId,
  startIndex = 0
): number => {
  const current = layout.elements[el || (layout.root as UniqueId)];

  if (current.type === DatasetLayoutViewModelType.SINGLE) {
    current.tabIndex = startIndex;
  }

  const lastTabIndex: number =
    current.type !== DatasetLayoutViewModelType.SINGLE
      ? (current.children || []).reduce(
          (last: number, currentEl: UniqueId) =>
            decorateDSInLayoutWithTabIndex(layout, currentEl, last),
          startIndex
        )
      : startIndex;

  return lastTabIndex + 1;
};

/**
 * Manages the creation of {@link CategoryViewModel} objects.
 *
 * This class encapsulates the common behavior, and delegates to other objects
 * that configure the varying parts.
 */
abstract class ViewModelFactory {
  protected constructor(
    private datasetTreeFactory: DatasetTreeFactory,
    private layoutStrategy: IDatasetLayoutStrategy,
    private intlProvider: CategoryIntlProvider
  ) {}

  /**
   * Creates a {@link CategoryViewModel} object using the objects the instance
   * was configured with.
   */
  public async createViewModel(
    layoutConfig?: LayoutConfig
  ): Promise<CategoryViewModel> {
    const datasetTree = await this.datasetTreeFactory.createDatasetTree();
    const layoutTree = this.layoutStrategy.doLayout(datasetTree);

    const title = await this.intlProvider.getCategoryTitle();
    const datasetTitlesTranslator = new DatasetTitlesTranslator(
      this.intlProvider
    );

    await datasetTitlesTranslator.fetchTitlesFor(datasetTree);

    const compositor = new ViewModelCompositor(
      layoutTree,
      datasetTitlesTranslator,
      layoutConfig
    );

    compositor.compose();

    const layout = {
      elements: compositor.getLayout(),
      root: compositor.getLayoutRoot(),
      treePanelSize: layoutConfig?.treePanelSize,
    };
    const datasets = compositor.getDatasets();

    const treeType = await this.getTreeType();

    decorateDSInLayoutWithTabIndex(
      layout,
      undefined,
      1 + +(treeType !== CategoryTreeType.NONE)
    );

    return {
      id: new UUIDService().generateId(),
      source: await this.createSource(),
      datasets,
      layout,
      treeType,
      title,
      processId: await new MerpsApiProvider().actions.generateProcessId(),
    };
  }

  /**
   * Factory method that creates a {@link CategoryViewModelSource} object.
   */
  protected abstract createSource(): Promise<CategoryViewModelSource>;

  /**
   * Template method that tells if {@link CategoryViewModel} has catalogs.
   */
  protected abstract getTreeType(): Promise<CategoryTreeType>;
}

export default ViewModelFactory;
